import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SaveIcon from '@mui/icons-material/Save';
import { format } from 'date-fns';
import Textarea from '@/trio-ui/Textarea';
import TransparentButton from '@/trio-ui/TransparentButton';
import UserInitials from '@/trio-ui/UserInitials';
import { TechnicalValidationCompetency, User } from '@/types/api';
import { getFirstAndLastNamesSplitted } from '@/utils/format';
import { TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER } from '@/utils/interviews';
import { AdditionalInfoInput, CompetencyBox, CollapsedView, ScoreInput } from './components';
import * as Styles from './TechnicalValidationReview.styles';
import DialogBox from '../DialogBox';

export type TechnicalValidationReviewComponentProps = {
  isEditing: boolean;
  isExpanded: boolean;
  reviewer?: User;
  competencies: TechnicalValidationCompetency[];
  overviewNote: string;
  reviewerScore: number;
  reviewedAt?: Date;
  isDeleteModalOpen: boolean;
  isActionLoading: boolean;
  onToggleIsEditing: () => void;
  onExpandView: () => void;
  onCollapseView: () => void;
  onSaveReview: () => void;
  onDeleteReview: () => void;
  onCancelEdition: () => void;
  onToggleDeleteModal: (event?: any) => void;
  onChangeScoreInput: (value: string, competencyName: string, inputName: string) => void;
  onChangeAdditionalInfoInput: (value: string, competencyName: string) => void;
  onChangeOverviewNote: (event: any) => void;
  isDirty: boolean;
};

export const REQUIRED_FIELD_MESSAGE = 'This field is required.';

export default function TechnicalValidationReviewComponent({
  competencies,
  overviewNote,
  reviewerScore,
  reviewer,
  reviewedAt,
  isEditing,
  isExpanded,
  isActionLoading,
  isDeleteModalOpen,
  onSaveReview,
  onToggleIsEditing,
  onExpandView,
  onCollapseView,
  onDeleteReview,
  onToggleDeleteModal,
  onCancelEdition,
  onChangeScoreInput,
  onChangeAdditionalInfoInput,
  onChangeOverviewNote,
  isDirty,
}: TechnicalValidationReviewComponentProps): JSX.Element {
  const [firstName, lastName] = getFirstAndLastNamesSplitted(reviewer?.name);

  const handleChangeScore = (event: any, competencyName: string, inputName: string): void => {
    const { value } = event.target;
    onChangeScoreInput(value, competencyName, inputName);
  };

  const handleChangeAdditionalInfo = (event: any, competencyName: string): void => {
    const { value } = event.target;
    onChangeAdditionalInfoInput(value, competencyName);
  };

  return (
    <>
      <Styles.Box isReviewed={!!reviewedAt} data-testid="technical-validation-review">
        {!!reviewedAt && (
          <Styles.ReviewerBox>
            <Styles.ReviewInfos>
              <UserInitials size={24} firstName={firstName} lastName={lastName} />
              <Styles.ReviewerName>{reviewer?.name}</Styles.ReviewerName>
              <Styles.ReviewTime>{format(reviewedAt, 'MMM d HH:MM  a')}</Styles.ReviewTime>
            </Styles.ReviewInfos>

            {isExpanded && (
              <Styles.ExpandBox onClick={onCollapseView}>
                Collapse
                <ExpandLessIcon />
              </Styles.ExpandBox>
            )}
          </Styles.ReviewerBox>
        )}

        {isExpanded ? (
          <>
            {!!competencies.length &&
              competencies.map((competency) => (
                <CompetencyBox
                  key={competency.name}
                  name={competency.name}
                  weight={competency.weight}
                  isReviewed={!!reviewedAt}
                >
                  {!!competency.scores.length &&
                    competency.scores.map((score) => {
                      const isBooleanInput = (score.options as string[])?.includes('Yes');
                      const isPlaceholderValue =
                        score.value === TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER;
                      const errorMessage =
                        (isDirty &&
                          !isBooleanInput &&
                          isPlaceholderValue &&
                          REQUIRED_FIELD_MESSAGE) ||
                        undefined;

                      return (
                        <ScoreInput
                          {...score}
                          key={score.name}
                          id={score.name}
                          isDisabled={!isEditing}
                          onChangeInput={(event) =>
                            handleChangeScore(event, competency.name, score.name)
                          }
                          errorMessage={errorMessage}
                        />
                      );
                    })}

                  <AdditionalInfoInput
                    key={`${competency.name}-additional-info`}
                    name={`${competency.name}-additional-info`}
                    value={competency.additionalInfo ?? ''}
                    isDisabled={!isEditing}
                    onChangeAddionalInfoInput={(event) =>
                      handleChangeAdditionalInfo(event, competency.name)
                    }
                  />
                </CompetencyBox>
              ))}

            <Styles.ReviewerScoreBox>
              <Styles.ReviewerScore>
                Reviewer Score: <span>{reviewerScore || '--'}</span>
              </Styles.ReviewerScore>
            </Styles.ReviewerScoreBox>

            <Styles.OverviewBox>
              <Textarea
                label="Overall Assessment (required)"
                id="note"
                name="technical-candidate-overview"
                value={overviewNote}
                onChange={onChangeOverviewNote}
                disabled={!isEditing}
                errorMessage={(isDirty && !overviewNote && REQUIRED_FIELD_MESSAGE) || undefined}
              />
            </Styles.OverviewBox>
          </>
        ) : (
          <CollapsedView
            overviewNote={overviewNote}
            reviewerScore={reviewerScore}
            onClickToExpand={onExpandView}
          />
        )}

        <Styles.ButtonsBox>
          {isEditing ? (
            <div>
              <TransparentButton
                isBlueButton
                icon={SaveIcon}
                onClick={onSaveReview}
                disabled={isActionLoading}
                data-testid="save-technical-validation-review"
              >
                Save review
              </TransparentButton>

              <TransparentButton
                icon={DeleteIcon}
                isRedButton
                onClick={onCancelEdition}
                disabled={isActionLoading}
              >
                Cancel
              </TransparentButton>
            </div>
          ) : (
            <div>
              <TransparentButton
                icon={EditIcon}
                onClick={onToggleIsEditing}
                disabled={isActionLoading || !isExpanded}
              >
                Edit review
              </TransparentButton>

              <TransparentButton
                icon={DeleteIcon}
                isRedButton
                onClick={onToggleDeleteModal}
                disabled={isActionLoading}
              >
                Delete review
              </TransparentButton>
            </div>
          )}

          {!!reviewedAt && isExpanded && (
            <Styles.ExpandBox onClick={onCollapseView}>
              Collapse
              <ExpandLessIcon />
            </Styles.ExpandBox>
          )}
        </Styles.ButtonsBox>
      </Styles.Box>

      <DialogBox
        isOpen={isDeleteModalOpen}
        onCancelHandler={onToggleDeleteModal}
        onConfirmHandler={onDeleteReview}
      />
    </>
  );
}
